import { createApi } from "@reduxjs/toolkit/query/react";
import { userApi } from "./userApi";
import { customBaseQuery } from "../../../utils/general";

export const projectsApiSlice = createApi({
  reducerPath: "projectApi",
  tagTypes: ["Project"],
  baseQuery: customBaseQuery,

  endpoints: (builder) => ({
    getProjects: builder.query({
      query: () => ({
        url: "/projects",
      }),
      providesTags: (result, error, arg) => [{ type: "Project", id: "LIST" }],
    }),
    getProjectById: builder.query({
      query: ({ id, token }) => ({
        url: token ? `/projects/${id}?token=${token}` : `/projects/${id}`,
      }),
      providesTags: (result, error, arg) => [
        { type: "Project", id: result?.project?._id },
        "Project",
      ],
    }),
    saveProject: builder.mutation({
      query: ({ body }) => ({
        url: "/projects/save",
        method: "POST",
        body,
      }),
      onCacheEntryAdded: (args, { dispatch }) => {
        dispatch(userApi.util.invalidateTags(["User"]));
      },
      invalidatesTags: [
        {
          type: "Project",
          id: "LIST",
        },
      ],
    }),
    editProject: builder.mutation({
      query: ({ body, id }) => ({
        url: `/projects/edit/${id}`,
        method: "PUT",
        body,
      }),
      onCacheEntryAdded: (args, { dispatch }) => {
        dispatch(userApi.util.invalidateTags(["User"]));
      },
      invalidatesTags: (result, error, arg) => [
        {
          type: "Project",
          id: arg.id,
        },
        "Project",
      ],
    }),

    publishProject: builder.mutation({
      query: ({ body, id }) => ({
        url: `/projects/publish/${id}`,
        method: "PUT",
        // body,
      }),
      onCacheEntryAdded: (args, { dispatch }) => {
        dispatch(userApi.util.resetApiState());
      },
      invalidatesTags: (result, err, arg) => [
        {
          type: "Project",
          id: arg?.id,
        },
        "Project",
      ],
    }),
    unPublishProject: builder.mutation({
      query: ({ id }) => ({
        url: `/projects/unpublish/${id}`,
        method: "PUT",
      }),
      onCacheEntryAdded: (args, { dispatch }) => {
        dispatch(userApi.util.resetApiState());
      },
      invalidatesTags: (result, err, arg) => [
        {
          type: "Project",
          id: arg?.id,
        },
        "Project",
      ],
    }),
    acceptProjectInvitation: builder.mutation({
      query: ({ body }) => ({
        url: `/projects/accept-invitation`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result) => [
        {
          type: "Project",
          id: result?.project_id,
        },
        "Project",
      ],
    }),
    sendInvitationEmail: builder.mutation({
      query: ({ body, id }) => ({
        url: `/projects/unregistered-invitation/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, err, arg) => [
        {
          type: "Project",
          id: arg?.id,
        },
        "Project",
      ],
    }),
    deleteUnregisteredInvitation: builder.mutation({
      query: ({ body, id }) => ({
        url: `/projects/delete-unregistered-invitation/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, err, arg) => [
        {
          type: "Project",
          id: arg?.id,
        },
        "Project",
      ],
    }),
    discardProject: builder.mutation({
      query: ({ id }) => ({
        url: `/projects/discard/${id}`,
        method: "POST",
      }),
      onCacheEntryAdded: (args, { dispatch }) => {
        dispatch(userApi.util.invalidateTags(["User"]));
      },
      invalidatesTags: (result, error, arg) => [
        {
          type: "Project",
          id: arg.id,
        },
        "Project",
      ],
    }),
    quickReport: builder.query({
      query: () => ({
        url: "/report/quick",
      }),
      providesTags: ["Project"],
    }),
    likeProject: builder.mutation({
      query: ({ id }) => ({
        url: `/projects/like/${id}`,
        method: "POST",
      }),
      // Don't invalidate to prevent unnecessary refetch
      invalidatesTags: [],
    }),
    dislikeProject: builder.mutation({
      query: ({ id }) => ({
        url: `/projects/dislike/${id}`,
        method: "POST",
      }),
      // Don't invalidate to prevent unnecessary refetch
      invalidatesTags: [],
    }),
    addProjectComment: builder.mutation({
      query: ({ id, body }) => ({
        url: `/projects/comments/add/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        {
          type: "Project",
          id: arg.id,
        },
        "Project",
      ],
    }),
    editProjectComment: builder.mutation({
      query: ({ id, body }) => ({
        url: `/projects/comments/edit/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        {
          type: "Project",
          id: arg.id,
        },
        "Project",
      ],
    }),
    deleteProjectComment: builder.mutation({
      query: ({ id }) => ({
        url: `/projects/comments/remove/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        {
          type: "Project",
          id: arg.id,
        },
        "Project",
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useSaveProjectMutation,
  useEditProjectMutation,
  usePublishProjectMutation,
  useAcceptProjectInvitationMutation,
  useSendInvitationEmailMutation,
  useDeleteUnregisteredInvitationMutation,
  useLazyGetProjectByIdQuery,
  useUnPublishProjectMutation,
  useQuickReportQuery,
  useDiscardProjectMutation,
  useDislikeProjectMutation,
  useLikeProjectMutation,
  useAddProjectCommentMutation,
  useEditProjectCommentMutation,
  useDeleteProjectCommentMutation,
} = projectsApiSlice;
